import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import {
  GetCirclebyID,
  GetWardsbyCircleId,
  GetZone,
} from "../../Apis/organizationApi";
import { AllShiftMaster } from "../../Apis/shiftMasterApi";
import { GetRoute } from "../../Apis/routeMasterApi";
import { AllOwnerTypeDetails } from "../../Apis/ownerTypeApi";
import { GetSCVCountDashboard } from "../../Apis/DashboardApis/ScvsDashbaordApi";
import Loader from "../../Common/Loader/loader";
import { getMasterAPIStore } from "../../../utils/getMasterAPIStore";
import { getNowDateTime, getOneMonthPastDateTime } from "../AssetVehicle/utils/helpers";


const ScvsDashboard = () => {
  const { zoneData, zoneLoading, circleData, circleLoading, wardData, wardLoading } = getMasterAPIStore();
  const [gvpCountDashboard, setGVPCountDashboard] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [startDate, setStartDate] = useState(getStartOfCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDateTime());
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedCircles, setSelectedCircles] = useState(null);
  const [loadingCircles, setLoadingCircles] = useState(false);
  const [circles, setCircles] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftData, setShiftData] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeData, setRouteData] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerData, setOwnerData] = useState([]);
  const [allWards, setAllWards] = useState([]);
  const [selectedWard, setSelectedWard] = useState(null);

  const fetchShift = async () => {
    try {
      const response = await AllShiftMaster();
      setShiftData(response);
    } catch (error) {
    }
  };

  const fetchRoute = async () => {
    try {
      const response = await GetRoute();
      setRouteData(response.data);
    } catch (error) {
    }
  };

  const fetchOwner = async () => {
    try {
      const response = await AllOwnerTypeDetails();
      setOwnerData(response);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchShift();
    fetchRoute();
    fetchOwner();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsOptionsLoading(true);

        const [gvpResponse] = await Promise.all([
          GetSCVCountDashboard(
            startDate,
            endDate,
            selectedZone,
            selectedCircles,
            selectedWard,
            selectedShift,
            selectedRoute,
            selectedOwner
          ),
        ]);

        const responseData = gvpResponse?.data || []; 
        const mergedData = responseData.reduce((acc, curr) => ({ ...acc, ...curr }), {});
        setGVPCountDashboard(mergedData);

        // Check if data is empty or all values are zero
        const isEmpty =
          Object.keys(mergedData).length === 0 ||
          Object.values(mergedData).every((val) => val === 0);

        if (!isEmpty) {
          // Format data for pie chart
          const pieData = Object.keys(mergedData).map((key, index) => ({
            name: key.replace(/([A-Z])/g, " $1").trim().toUpperCase(), // Convert to uppercase and add spaces
            value: mergedData[key],
            color: COLORS[index % COLORS.length],
          }));
          setChartData(pieData);
        } else {
          setChartData([]); // No data case
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsOptionsLoading(false);
      }
    };

    // Fetch data only if start and end dates are selected
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, selectedZone, selectedCircles, selectedWard, selectedShift, selectedRoute, selectedOwner]);

  useEffect(() => {
    const fetchWardsByCircle = async () => {
      try {
        if (selectedCircles) {
          // Fetch wards based on the selected circle ID
          const wardsData = await GetWardsbyCircleId(selectedCircles);
          setAllWards(wardsData); // Assuming wards data is in the format you expect
        } else {
          setAllWards([]); // Reset wards if no circle is selected
        }
      } catch (error) {
        console.error("Error fetching wards data:", error);
      }
    };

    fetchWardsByCircle();
  }, [selectedCircles]);

  const displayNames = {
    activeLE: (
      <>
        Active
        Loading Equipments
      </>
    ),
    activeScv: "Active SCVs",
    condemed: "Condemned SCVs",
    condemedLE: (
      <>
        Condemned SCVs
        Loading Equipments
      </>
    ),
    deployedScv: "Deployed SCVs",
    leAssigned: (
      <>
        Assigned
        Loading Equipments
      </>
    ),
    // routesAssigned: "Routes Assigned",
    // routesCompleted: "Routes Completed",
    // routesInProgress: "Routes In Progress",
    totalLE: (
      <>
        Total
        Loading Equipments
      </>
    ),
    // totalRoutes: "Total Routes",
    totalScv: "Total SCVs",
  };


  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

  return (
    <div style={{ width: "100%" }}>
      <div className="content">
        <div className="page-content">
          <h4 className="text-start mb-3">SCVs Dashboard</h4>
          <div className="card">
            <div className="card-body">


              <div className="row " style={{ textAlign: "justify" }}>
                <div className="col-md-12">
                  <form>
                    <div className="row" style={{ textAlign: "justify" }}>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="startDate">From Date & Time</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            id="startDate"
                            name="startDate"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            value={startDate}
                            min={getOneMonthPastDateTime()}
                            max={getNowDateTime()}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="endDate">To Date & Time</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            id="endDate"
                            name="endDate"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            value={endDate}
                            onChange={(e) => {
                              // Check if end date is before start date
                              if (new Date(e.target.value) < new Date(startDate)) {
                                alert("End date cannot be before start date");
                                return; // Don't update end date if it's invalid
                              }
                              setEndDate(e.target.value);
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="ZoneId">Select Zone</label>
                          <select
                            type="text"
                            name="ZoneId"
                            className="form-select"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            value={selectedZone}
                            onChange={(e) => {
                              if (e.target.value === "") {
                                // Clear selected circle and transfer station when "All Zones" is selected
                                setSelectedCircles("");
                              }
                              setSelectedZone(e.target.value);
                            }}
                          >
                            <option value="">All</option>
                            {zoneData.filter((zone)=>zone.isActive).map((zone) => (
                              <option key={zone.zoneId} value={zone.zoneId}>
                                {zone.zoneName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="CircleId">Select Circle</label>
                          <select
                            type="text"
                            name="CircleId"
                            className="form-select"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            value={selectedCircles}
                            onChange={(e) => {
                              const selectedCircleId = e.target.value;
                              setSelectedCircles(selectedCircleId);
                            }}
                          >
                            <option value="">All</option>
                            {circleData.filter((circle)=>selectedZone ? (circle.zoneId==selectedZone) : circle).map((circle) => (
                              <option
                                key={circle.circleId}
                                value={circle.circleId}
                                selected={circle.circleId === selectedCircles}
                              >
                                {circle.circleName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="WardId">Select Ward</label>
                          <select
                            type="text"
                            name="WardId"
                            className="form-select"
                            style={{ backgroundColor: "#FFF" }}
                            value={selectedWard}
                            onChange={(e) => {
                              setSelectedWard(e.target.value);
                            }}
                          >
                            <option value="">All</option>
                            {wardData.filter((ward)=>selectedCircles ? (ward.circleId==selectedCircles) : ward).map((ward) => (
                              <option
                                key={ward.wardId}
                                value={ward.wardId}
                                selected={ward.wardId === selectedWard}
                              >
                                {ward.wardName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4 ">
                        <div className="form-group">
                          <label className="form-label">Select Shift</label>
                          <select
                            value={selectedShift}
                            name="ShiftId"
                            className="form-select"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            onChange={(e) => {
                              setSelectedShift(e.target.value);
                            }}
                          >
                            <option value="">All</option>
                            {Array.isArray(shiftData) &&
                              shiftData.map((shift) => (
                                <option
                                  key={shift.shiftId}
                                  value={shift.shiftId}
                                  selected={shift.shiftId === selectedShift}
                                >
                                  {shift.shiftName}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4 ">
                        <div className="form-group">
                          <label className="form-label">Select Route</label>
                          <select
                            value={selectedRoute}
                            name="RouteID"
                            className="form-select"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            onChange={(e) => {
                              setSelectedRoute(e.target.value);
                            }}
                          >
                            <option value="">All</option>
                            {Array.isArray(routeData) &&
                              routeData.map((route) => (
                                <option
                                  key={route.routeId}
                                  value={route.routeId}
                                  selected={route.routeId === selectedRoute}
                                >
                                  {route.routeCode}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-4 ">
                        <div className="form-group">
                          <label className="form-label">Select Owner</label>
                          <select
                            value={selectedOwner}
                            name="OwnerTypeId"
                            className="form-select"
                            style={{ backgroundColor: "#FFF", borderRadius: "0px" }}
                            onChange={(e) => {
                              setSelectedOwner(e.target.value);
                            }}
                          >
                            <option value="">All</option>
                            {Array.isArray(ownerData) &&
                              ownerData.filter((owner)=>owner.isActive).map((owner) => (
                                <option
                                  key={owner.ownerTypeId}
                                  value={owner.ownerTypeId}
                                  selected={owner.ownerTypeId === selectedOwner}
                                >
                                  {owner.ownerTypeName}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div></div>
          {isOptionsLoading && <Loader />}
          <div className="row mt-3" style={{ textAlign: "justify" }}>
            <div className="col-md-12">
              <div className="row scv-cards g-3" style={{ textAlign: "justify" }}>
                {Object.entries(gvpCountDashboard).map(
                  ([GVPDashboard, count]) => {
                    const displayName =
                      displayNames[GVPDashboard] || GVPDashboard;
                    return (

                      <div className="col-md-3">
                        <div className="card shadow">
                          <div className="card-body card_text_no">
                            <p className="card-title">{displayName}</p>
                            <h4 className="card-text">{count}</h4>
                          </div>
                        </div>
                      </div>

                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-3" >
          <div className="col-md-12 m-auto">
            <div className="card">
              <div className="card-header">
                SCVs Dashboard Chart
              </div>
              <div className="card-body text-center">
                {chartData.length > 0 ? (
                  <PieChart width={800} height={450} className="mx-auto">
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={150}
                      fill="#8884d8"
                      label
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                ) : (
                  <p style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold",}}>
                    No Preview available as data does not exists
                  </p>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default ScvsDashboard;

const getStartOfCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00`;
};

// Function to get current date and time in the required format
const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
};
